import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    Box, TextField, MenuItem, Button, Stack, Typography, 
    Paper, Collapse, CircularProgress, Alert, Container, LinearProgress, InputAdornment,
    List, ListItem, ListItemIcon, ListItemText, ListItemSecondaryAction, IconButton,
    Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import { configService, ticketService } from '../api';

const ExternalTicketForm = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [projects, setProjects] = useState([]);
    const [functionalities, setFunctionalities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [message, setMessage] = useState({ text: '', type: '' });
    const [openSuccessDialog, setOpenSuccessDialog] = useState(false);
    const [createdTicketId, setCreatedTicketId] = useState(null);
    const [files, setFiles] = useState([]);
    const [isDragging, setIsDragging] = useState(false);

    const [formData, setFormData] = useState({
        email: '',
        project: '',
        subject: '',
        functionality: '',
        description: ''
    });

    const getFriendlyErrorMessage = (err, fallback) => {
        if (err.response) {
            const status = err.response.status;
            const data = err.response.data;

            if (status === 400 && data) {
                if (typeof data === 'object') {
                    // Extraer el primer mensaje de error de cualquier campo
                    const firstError = Object.values(data)[0];
                    if (Array.isArray(firstError)) return firstError[0];
                    if (typeof firstError) return firstError;
                    return data.detail || data.error || fallback;
                }
                return typeof data === 'string' ? data : fallback;
            }
            
            if (status >= 500) return "Error interno del servidor. Por favor, intente más tarde.";
        }
        return fallback;
    };

    useEffect(() => {
        configService.getProjects()
            .then(res => setProjects(res.data.results || res.data))
            .catch(() => setMessage({ text: 'Error al cargar proyectos', type: 'error' }))
            .finally(() => setLoading(false));
    }, []);

    // Cargar funcionalidades cuando se selecciona un proyecto
    useEffect(() => {
        if (formData.project) {
            configService.getFunctionalities(formData.project)
                .then(res => setFunctionalities(res.data.results || res.data))
                .catch(() => setFunctionalities([]));
        } else {
            setFunctionalities([]);
        }
    }, [formData.project]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e) => {
        const selectedFiles = Array.from(e.target.files);
        setFiles(prev => [...prev, ...selectedFiles]);
    };

    const removeFile = (index) => {
        setFiles(prev => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const ticketData = {
                title: formData.subject,
                description: formData.description,
                project: formData.project,
                functionality: formData.functionality || null,
                submitter_email_external: formData.email
            };
            const res = await ticketService.submitExternalTicket(ticketData);
            
            // Subir archivos adjuntos si existen
            if (files.length > 0) {
                for (const file of files) {
                    const attachData = new FormData();
                    attachData.append('ticket', res.data.id);
                    attachData.append('file', file);
                    await ticketService.uploadAttachment(attachData);
                }
            }

            setCreatedTicketId(res.data.id);
            setOpenSuccessDialog(true);
            setMessage({ text: '', type: '' }); // Limpiar posibles errores previos
            setFormData({ email: '', project: '', subject: '', functionality: '', description: '' });
            setFiles([]);
        } catch (err) {
            console.error("Error detallado:", err.response?.data || err.message);
            const errorMsg = getFriendlyErrorMessage(err, 'Error al enviar el ticket. Intente de nuevo.');
            setMessage({ text: errorMsg, type: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    // Lógica de visibilidad progresiva
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isEmailValid = emailRegex.test(formData.email);
    
    const showProject = isEmailValid;
    const showSubject = showProject && formData.project !== '';
    const showFunctionality = showSubject && functionalities.length > 0;
    const showDescription = showSubject && formData.subject.trim().length >= 5;
    const showSubmit = showDescription && formData.description.trim().length >= 10;

    // Cálculo del progreso de avance
    const progress = useMemo(() => {
        let p = 0;
        if (isEmailValid) p += 25;
        if (formData.project) p += 25;
        if (formData.subject.trim().length >= 5) p += 25;
        if (formData.description.trim().length >= 10) p += 25;
        // Si ya se envió con éxito, lo dejamos en 100
        if (message.type === 'success') return 100;
        return p;
    }, [isEmailValid, formData, message.type]);

    const showAttachments = progress === 100 && message.type !== 'success';

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Container maxWidth="sm" sx={{ py: 4 }}>
            <Paper sx={{ p: 4, borderRadius: 3 }} elevation={3}>
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                    {t('form_support_title')}
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 4 }}>
                    {t('form_support_subtitle')}
                </Typography>

                {/* Barra de Avance */}
                <Box sx={{ mb: 4 }}>
                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                        <Typography variant="caption" color="textSecondary" fontWeight="bold">
                            {progress}% {t('completed') || 'Completado'}
                        </Typography>
                    </Stack>
                    <LinearProgress 
                        variant="determinate" 
                        value={progress} 
                        sx={{ height: 8, borderRadius: 5, bgcolor: 'grey.200' }}
                    />
                </Box>

                {message.text && (
                    <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage({ text: '', type: '' })}>
                        {message.text}
                    </Alert>
                )}

                <Box component="form" onSubmit={handleSubmit}>
                    <Stack spacing={3}>
                        {/* 1. Email: Siempre visible */}
                        <TextField
                            fullWidth
                            label={t('form_email')}
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            placeholder="correo@ejemplo.com"
                            InputProps={{
                                endAdornment: isEmailValid && (
                                    <InputAdornment position="end">
                                        <CheckCircleIcon color="success" fontSize="small" />
                                    </InputAdornment>
                                )
                            }}
                        />

                        {/* 2. Proyecto: Aparece tras email válido */}
                        <Collapse in={showProject}>
                            <TextField
                                select
                                fullWidth
                                label={t('form_project')}
                                name="project"
                                value={formData.project}
                                onChange={handleChange}
                                required={showProject}
                                InputProps={{
                                    endAdornment: formData.project && (
                                        <InputAdornment position="end" sx={{ mr: 2 }}>
                                            <CheckCircleIcon color="success" fontSize="small" />
                                        </InputAdornment>
                                    )
                                }}
                            >
                                {projects.map((p) => (
                                    <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                                ))}
                            </TextField>
                        </Collapse>

                        {/* 3. Asunto: Aparece tras seleccionar proyecto */}
                        <Collapse in={showSubject}>
                            <TextField
                                fullWidth
                                label={t('form_subject')}
                                name="subject"
                                value={formData.subject}
                                onChange={handleChange}
                                required={showSubject}
                                InputProps={{
                                    endAdornment: formData.subject.trim().length >= 5 && (
                                        <InputAdornment position="end">
                                            <CheckCircleIcon color="success" fontSize="small" />
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </Collapse>

                        {/* 4. Funcionalidad: Solo si el proyecto tiene catálogo */}
                        <Collapse in={showFunctionality}>
                            <TextField
                                select
                                fullWidth
                                label={t('form_functionality')}
                                name="functionality"
                                value={formData.functionality}
                                onChange={handleChange}
                                InputProps={{
                                    endAdornment: formData.functionality && (
                                        <InputAdornment position="end" sx={{ mr: 2 }}>
                                            <CheckCircleIcon color="success" fontSize="small" />
                                        </InputAdornment>
                                    )
                                }}
                            >
                                <MenuItem value="">{t('none') || 'Ninguna'}</MenuItem>
                                {functionalities.map((f) => (
                                    <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>
                                ))}
                            </TextField>
                        </Collapse>

                        {/* 5. Descripción: Aparece tras el asunto */}
                        <Collapse in={showDescription}>
                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                label={t('form_description')}
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                required={showDescription}
                                InputProps={{
                                    endAdornment: formData.description.trim().length >= 10 && (
                                        <InputAdornment position="end">
                                            <CheckCircleIcon color="success" fontSize="small" />
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </Collapse>

                        {/* 6. Adjuntar Archivos: Solo al 100% de progreso */}
                        <Collapse in={showAttachments}>
                            <Box sx={{ mt: 1 }}>
                                <Typography variant="subtitle2" color="primary" fontWeight="bold" gutterBottom>
                                    ¿Deseas adjuntar documentos? (Opcional)
                                </Typography>
                                <Typography variant="caption" color="textSecondary" sx={{ mb: 2, display: 'block' }}>
                                    Nota: Los documentos son opcionales y no son necesarios para radicar un ticket.
                                </Typography>
                                
                                <Paper
                                    variant="outlined"
                                    onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                                    onDragLeave={() => setIsDragging(false)}
                                    onDrop={(e) => {
                                        e.preventDefault();
                                        setIsDragging(false);
                                        const droppedFiles = Array.from(e.dataTransfer.files);
                                        setFiles(prev => [...prev, ...droppedFiles]);
                                    }}
                                    sx={{
                                        p: 3,
                                        textAlign: 'center',
                                        cursor: 'pointer',
                                        bgcolor: isDragging ? 'action.hover' : 'background.paper',
                                        borderStyle: 'dashed',
                                        borderColor: isDragging ? 'primary.main' : 'divider',
                                        transition: 'all 0.2s'
                                    }}
                                    onClick={() => document.getElementById('file-input').click()}
                                >
                                    <input
                                        id="file-input"
                                        type="file"
                                        multiple
                                        hidden
                                        onChange={handleFileChange}
                                    />
                                    <CloudUploadIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                                    <Typography variant="body2">
                                        Arrastra archivos aquí o <strong>haz clic para buscar</strong>
                                    </Typography>
                                </Paper>

                                {files.length > 0 && (
                                    <List size="small" sx={{ mt: 1 }}>
                                        {files.map((file, index) => (
                                            <ListItem 
                                                key={index}
                                                sx={{ 
                                                    bgcolor: 'grey.50', 
                                                    borderRadius: 1, 
                                                    mb: 0.5,
                                                    border: '1px solid',
                                                    borderColor: 'divider'
                                                }}
                                            >
                                                <ListItemIcon sx={{ minWidth: 36 }}>
                                                    <CheckCircleIcon color="success" fontSize="small" />
                                                </ListItemIcon>
                                                <ListItemText 
                                                    primary={file.name} 
                                                    primaryTypographyProps={{ variant: 'caption', noWrap: true }} 
                                                />
                                                <ListItemSecondaryAction>
                                                    <IconButton edge="end" size="small" onClick={() => removeFile(index)}>
                                                        <DeleteIcon fontSize="inherit" />
                                                    </IconButton>
                                                </ListItemSecondaryAction>
                                            </ListItem>
                                        ))}
                                    </List>
                                )}
                            </Box>
                        </Collapse>

                        {/* 6. Botón enviar: Aparece cuando todo está listo */}
                        <Collapse in={showSubmit}>
                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                size="large"
                                disabled={submitting}
                                sx={{ py: 1.5, fontWeight: 'bold' }}
                            >
                                {submitting ? <CircularProgress size={24} /> : t('form_submit_ticket')}
                            </Button>
                        </Collapse>
                    </Stack>
                </Box>
            </Paper>

            {/* Pop-up de confirmación de radicación exitosa */}
            <Dialog open={openSuccessDialog} onClose={() => setOpenSuccessDialog(false)} maxWidth="xs" fullWidth>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 2, mb: 2 }}>
                        <CheckCircleIcon color="success" sx={{ fontSize: 70, mb: 2 }} />
                        <Typography variant="h5" align="center" sx={{ fontWeight: 'bold' }}>
                            ¡Ticket Radicado Exitosamente!
                        </Typography>
                    </Box>
                    <Typography variant="body1" align="center" sx={{ mb: 2 }}>
                        Su ticket <strong>#{createdTicketId}</strong> ha sido enviado correctamente.
                    </Typography>
                    <Typography variant="body2" align="center" color="textSecondary">
                        Le informamos que podrá hacer seguimiento de su solicitud en el módulo <strong>Seguimiento</strong> utilizando su correo electrónico y el ID de su ticket.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center', pb: 4, px: 3, flexDirection: 'column', gap: 1.5 }}>
                    <Button 
                        fullWidth 
                        variant="contained" 
                        onClick={() => navigate('/external-followup')}
                        sx={{ py: 1.2, fontWeight: 'bold' }}
                    >
                        Ir al módulo Seguimiento
                    </Button>
                    <Button 
                        fullWidth 
                        variant="outlined" 
                        onClick={() => setOpenSuccessDialog(false)}
                        sx={{ py: 1.2 }}
                    >
                        Enviar un nuevo ticket
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default ExternalTicketForm;